import React, { useMemo } from 'react';
import { useIntl } from 'umi';
import { Table, Button, Segment } from 'tea-component';
import TableContent from '@sg/components/TableContent';
import AutoSaveModal from '@/pages/strategic/components/AutoSaveModal';
import RoutePrompt from '@/pages/strategic/components/RoutePrompt';
import { getOptionsSegment } from '../../pageConfig';
import { usePhaseStudioTable } from './hooks/usePhaseStudioTable';
import { hasExpandableChildren, isCategoryRow } from './utils';

interface IPhaseStudioTableProps {
  activeCard: 'Revenue' | 'Profit';
  selectedMonth: string | null;
  canEdit?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

const PhaseStudioTable: React.FC<IPhaseStudioTableProps> = ({
  activeCard,
  selectedMonth,
  canEdit = false,
  onEditingChange,
}) => {
  const { formatMessage } = useIntl();
  const { expandable, injectable, mergeable } = Table.addons;

  const optionsSegment = useMemo(() => getOptionsSegment(formatMessage), [formatMessage]);

  const {
    tableRef,
    isEditing,
    studioBreakdownData,
    studioBreakdownLoading,
    originalStudioBreakdownData,
    activeDateTypeSegment,
    expandedKeys,
    handleExpandedKeysChange,
    phaseColumns,
    phaseEditColumns,
    fetchStudioBreakdownData,
    editStudioBreakdownData,
    calculateMaxVsVallue,
    handleEditingChange,
    handleEditDataChange,
    hasUnsavedChanges,
    autoSaveVisible,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    handleEditClick,
    handleCancelClick,
    handleSegmentChange,
  } = usePhaseStudioTable({
    activeCard,
    selectedMonth,
    onEditingChange,
  });

  const tableAddons = useMemo(() => [
    expandable({
      // 已经展开的产品
      expandedKeys,
      // 产品展开为消息行
      expand: record => (hasExpandableChildren(record) ? record.children : null),
      // 发生展开行为时，回调更新展开键值
      onExpandedKeysChange: handleExpandedKeysChange,
      // 只有含 studio 子行的 category 允许展开
      shouldRecordExpandable: record => !isEditing && hasExpandableChildren(record),
      targetColumnKey: 'Phase',
      rowExpand: true,
    }),
    injectable({
      row: (props, context) => ({
        style: {
          ...(props.style || {}),
          background: context.recordIndex === 0
            ? '#E4E2F7'
            : isCategoryRow(context.record)
              ? '#F5F5F8'
              : '',
        },
      }),
    }),
    mergeable({
      headColSpan: columnIndex => {
        switch (columnIndex) {
          case 4: // "25F VS 25B" (负值列)
            return 2; // 合并两列
          case 5: // "25F VS 25B2" (正值列)
            return 0; // 被合并
          default:
            return 1;
        }
      },
    }),
  ], [expandable, injectable, mergeable, expandedKeys, handleExpandedKeysChange, isEditing]);

  return (
    <>
      {/* Phase Studio Table */}
      <div className='dbs-table-phase' key={activeCard}>
        {/* Row 1: Segment (left) + Edit/Confirm+Cancel buttons (right) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <Segment
              className="dbs-gradient-segment"
              options={optionsSegment}
              value={activeDateTypeSegment}
              disabled={isEditing}
              onChange={handleSegmentChange}
            />
          </div>
          {canEdit && (
            <div>
              {!isEditing && (
                <Button
                  onClick={handleEditClick}
                  style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                  className="dbs-gradient-btn"
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {isEditing && (
                <div className="dbs-weekly-buttons-wrapper">
                  <Button
                    onClick={() => {
                      tableRef.current?.handleSave();
                    }}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={handleCancelClick}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        {isEditing && (
          <div style={{ marginBottom: '18px', color: '#262832', fontSize: '14px' }}>
            {formatMessage({ id: 'dbs_25A_25F_25B_data_notice' })}
          </div>
        )}
        {/* Phase Table */}
        <TableContent
          ref={tableRef}
          className="dbs-phase-table"
          // height={'360px'}
          recordKey={(record: any) =>
            // 这里要注意,原始数据中数据类型全是字符串，不转成字符串会导致 key 值匹配不上
            String(record.id)
          }
          datas={studioBreakdownData}
          loading={studioBreakdownLoading}
          columns={phaseColumns}
          editColumns={phaseEditColumns}
          onCancelRefetch={fetchStudioBreakdownData}
          originalData={originalStudioBreakdownData}
          lastResultData={(data: any) => calculateMaxVsVallue(data)}
          addons={tableAddons}
          onSave={editStudioBreakdownData}
          editable={true}
          userPermissions={{ canEdit: true }}
          hideButtons={true}
          onEditingChange={handleEditingChange}
          onEditDataChange={handleEditDataChange}
        />
      </div>

      {/* Auto-save Modal for Phase Table */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={handleAutoSaveConfirm}
        onClose={handleAutoSaveCancel}
      />

      {/* RoutePrompt to intercept navigation when there are unsaved changes in Phase Table */}
      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          // Save Phase Table changes before navigating away
          if (tableRef.current?.handleSave) {
            await tableRef.current.handleSave();
          }
        }}
      />
    </>
  );
};

export default PhaseStudioTable;
